import { io, Socket } from 'socket.io-client';
import { dotenv } from '@/utils/constants/env';

type EventHandler = (data: unknown) => void;

/** High-frequency streaming-audio traffic that fires many times per second. Logging each one floods
 *  the console and hides the messages that are actually useful for debugging, so we skip it.
 *  Covers inbound audio events (`agent:audio`, `agent_realtime:audio`, `onboarding:audio`) and
 *  outbound audio chunks (`*.audio_chunk` on the generic `message` event). */
function isNoisyMessage(event: string, data: unknown): boolean {
  if (event.endsWith(':audio')) return true;
  const type = data && typeof data === 'object' ? (data as { type?: unknown }).type : undefined;
  return typeof type === 'string' && type.includes('audio_chunk');
}

class SocketService {
  private static instance: SocketService;
  private socket: Socket | null = null;
  private handlers: Map<string, EventHandler[]> = new Map();
  // Guard against double-connect from React StrictMode double-invoke or rapid token changes.
  // Set synchronously when io() is called; cleared on connect/error/disconnect.
  private _isConnecting = false;
  // Token the current/in-flight socket was authenticated with. The gateway decodes this once
  // at `connect` and freezes user_id/tenant_id on the connection record for its lifetime — so
  // connect() must reconnect whenever the token changes (refresh, tenant switch), not no-op.
  private _currentToken: string | null = null;
  // Called when the gateway rejects the handshake for auth reasons (expired/invalid token).
  // App.tsx wires this to a token refresh; the refreshed token re-runs connect().
  private _onAuthError: (() => void) | null = null;

  setAuthErrorHandler(handler: (() => void) | null): void {
    this._onAuthError = handler;
  }

  static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  connect(token: string): void {
    // Already connected or mid-connect with this exact token — nothing to do. If the token
    // differs (refresh, tenant switch), fall through and reconnect below even though a socket
    // is already up — otherwise the gateway keeps using the identity/tenant_id it decoded from
    // the OLD token for the rest of the tab's lifetime.
    if (this._currentToken === token && (this.socket?.connected || this._isConnecting)) return;

    console.info('[SocketService] connect() called | url=%s tokenChanged=%s', dotenv.WS_URL, this._currentToken !== null && this._currentToken !== token);

    // Tear down any existing socket — stale, mid-connect, or still authenticated with a
    // previous token — before establishing a fresh connection with the current token.
    // Do NOT clear this.handlers — registered listeners survive a reconnect so
    // they get re-attached to the fresh socket below.
    if (this.socket) {
      console.info('[SocketService] cleaning up existing socket before reconnect');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this._currentToken = token;
    this._isConnecting = true;

    this.socket = io(dotenv.WS_URL, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 2000,
    });

    // Re-attach any handlers registered before this connect() call.
    // React runs child effects before parent effects, so socketService.on()
    // may be called by child components before App.tsx calls connect().
    if (this.handlers.size > 0) {
      console.info('[SocketService] re-attaching %d pre-registered event handlers', this.handlers.size);
      this.handlers.forEach((_, event) => {
        this.socket!.on(event, (data: unknown) => {
          if (!isNoisyMessage(event, data)) console.debug('[SocketService] ← recv event=%s', event, data);
          this.handlers.get(event)?.forEach((h) => h(data));
        });
      });
    }

    this.socket.on('connect', () => {
      this._isConnecting = false;
      console.info('[SocketService] ✓ connected | id=%s', this.socket?.id);
    });

    this.socket.on('connect_error', (err) => {
      this._isConnecting = false;
      const errData = err as unknown as Record<string, unknown>;
      console.error('[SocketService] ✗ connect_error | message=%s data=%o', err.message, errData.data ?? err);
      // For auth errors (expired/invalid token), stop reconnection so the caller
      // can supply a fresh token via connect(). Token refresh is handled by
      // RTK Query's baseQuery which updates Redux → App.tsx calls connect() again.
      const msg = err.message?.toLowerCase() ?? '';
      if (msg.includes('expired') || msg.includes('invalid') || msg.includes('unauthorized') || msg.includes('auth')) {
        console.warn('[SocketService] Auth error — stopping reconnection. Waiting for token refresh.');
        this.socket?.disconnect();
        this._onAuthError?.();
      }
    });

    this.socket.on('disconnect', (reason) => {
      this._isConnecting = false;
      console.info('[SocketService] disconnected | reason=%s', reason);
      // socket.io's built-in reconnection logic does NOT kick in for 'io server disconnect'
      // (the gateway deliberately closed the connection — e.g. a graceful pod restart/deploy) —
      // per socket.io semantics that reason means "don't auto-retry, the server meant to drop you".
      // Every other reason ('transport close', 'transport error', 'ping timeout', …) already
      // auto-reconnects. Without this, a graceful gateway restart leaves the client offline
      // until something else (token refresh, tab refocus, manual refresh) calls connect() again.
      if (reason === 'io server disconnect') {
        console.info('[SocketService] server-initiated disconnect — resuming reconnection');
        this.socket?.connect();
      }
    });
  }

  disconnect(): void {
    console.info('[SocketService] disconnect() called');
    this._isConnecting = false;
    this._currentToken = null;
    this.socket?.disconnect();
    this.socket = null;
    // Do NOT clear this.handlers — React hook registrations (on/off) are independent of the
    // socket lifecycle. connect() re-attaches all registered handlers to the new socket.
  }

  on(event: string, handler: EventHandler): void {
    const socketState = this.socket ? (this.socket.connected ? 'connected' : 'connecting') : 'null';
    if (!this.handlers.has(event)) {
      this.handlers.set(event, []);
      if (this.socket) {
        // Socket already exists — attach listener immediately.
        this.socket.on(event, (data: unknown) => {
          if (!isNoisyMessage(event, data)) console.debug('[SocketService] ← recv event=%s', event, data);
          this.handlers.get(event)?.forEach((h) => h(data));
        });
        console.debug('[SocketService] on(%s) registered — socket=%s', event, socketState);
      } else {
        // Socket not yet created; connect() will re-attach when called.
        console.warn('[SocketService] on(%s) — socket is null, will re-attach on connect()', event);
      }
    }
    this.handlers.get(event)!.push(handler);
  }

  off(event: string, handler: EventHandler): void {
    const list = this.handlers.get(event);
    if (list) {
      this.handlers.set(event, list.filter((h) => h !== handler));
    }
  }

  emit(event: string, data: unknown): void {
    const connected = this.socket?.connected ?? false;
    const id = this.socket?.id ?? 'none';
    if (!isNoisyMessage(event, data)) {
      console.debug('[SocketService] → emit event=%s | connected=%s id=%s', event, connected, id, data);
    }
    if (!this.socket) {
      console.error('[SocketService] emit(%s) DROPPED — socket is null', event);
      return;
    }
    this.socket.emit(event, data);
  }

  /** Ack-based emit for sends that must never silently vanish (e.g. chat messages). Rejects on
   *  timeout or a socket/gateway-reported error instead of firing into the void — the caller can
   *  then show the user an inline failure instead of the message just disappearing. */
  emitWithAck<T = unknown>(event: string, data: unknown, timeoutMs = 8000): Promise<T> {
    if (!this.socket) {
      console.error('[SocketService] emitWithAck(%s) DROPPED — socket is null', event);
      return Promise.reject(new Error('socket-not-connected'));
    }
    return this.socket.timeout(timeoutMs).emitWithAck(event, data) as Promise<T>;
  }

  /** Fire registered listeners for `event` locally without sending to the server. */
  localEmit(event: string, data: unknown): void {
    this.handlers.get(event)?.forEach((h) => h(data));
  }

  get isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

export const socketService = SocketService.getInstance();
